import {
  Box,
  Button,
  Flex,
  FlexProps,
  Grid,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { socket } from "../socket";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSimpleToast } from "../hooks/toast.hooks";
import { useConnectedUser, useUser } from "../hooks/user.hooks";
import { formatDistanceToNow } from "date-fns";
import SendIcon from "./icons/SendIcon";
import AttachedFileIcon from "./icons/AttachedFileIcon";
import AttachedFileModal from "./modals/AttachedFileModal";
import { useMutation, useQueryClient } from "react-query";
import { linkFilesToTicket, updateTicket } from "../mutations/ticket.mutations";
import SimpleModal from "./modals/SimpleModal";
import LabelsIcon from "./icons/LabelsIcon";
import TicketStatusForm, {
  TicketStatusFormHandler,
} from "./forms/TicketStatusForm";
import { useTicket } from "../hooks/tickets.hooks";
import { TicketStatus } from "../types/ticket.types";
import { userIsAdmin } from "../utils";

type TicketMeta = {
  room: {
    name: string;
  };
  author: {
    id: number | null;
  };
  ticket: {
    id: number | null;
  };
};

type TicketMessage = {
  content: string;
  createdAt: Date;
  updatedAt: Date;
  authorId: number;
};

const ChatMessage = ({ message }: { message: TicketMessage }) => {
  const user = useConnectedUser();
  const { data: author } = useUser(message.authorId);

  if (!user || !author) {
    return <></>;
  }
  const isAuthor = user.id === author.id;
  return (
    <Grid templateColumns={"3rem calc(100% - 6rem) 3rem"}>
      <Box></Box>
      <Flex w="100%" justifyContent={isAuthor ? "end" : "start"}>
        <Box w="fit-content" minW={"20rem"}>
          <Flex>
            <Box
              bgColor={isAuthor ? "gray.100" : "brand.100"}
              w="100%"
              p={4}
              borderRadius={"xl"}
              mt={1}
            >
              <Box whiteSpace={"pre-line"}>{message.content}</Box>
              <Flex
                justifyContent={"end"}
                lineHeight="18px"
                fontSize="12px"
                fontWeight="400"
                color="gray.600"
                mt={2}
              >
                {formatDistanceToNow(message.createdAt)}
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Flex>
      <Box></Box>
    </Grid>
  );
};

const ChatAuthorName = ({ id }: { id: number }) => {
  const { data: author } = useUser(id);
  const user = useConnectedUser()

  if (author && user && author.id === user?.id) {
    return null
  }
  return (
    <Grid templateColumns={"3rem calc(100% - 6rem) 3rem"}>
      <Box></Box>
      { author?.isSuperAdmin === false && <Text lineHeight="21px" fontSize="16px" fontWeight="600" mt={3}>
        {author?.firstName} {author?.lastName}
      </Text> }
      { author?.isSuperAdmin === true && <Text lineHeight="21px" fontSize="16px" fontWeight="600" mt={3}>
        Supembouts ({author?.firstName} {author?.lastName})
      </Text> }
      <Box></Box>
    </Grid>
  );
};

const ChatMessages = ({ messages }: { messages: TicketMessage[] }) => {
  let prevAuthorId: number | null = null;
  return messages.map((message, index) => {
    const showName = prevAuthorId === null || message.authorId !== prevAuthorId;
    if (showName) {
      prevAuthorId = message.authorId;
    }
    return (
      <>
        {showName && <ChatAuthorName id={message.authorId}></ChatAuthorName>}
        <ChatMessage key={index} message={message}></ChatMessage>
      </>
    );
  });
};

const Chat = ({
  defaultRoom,
  ticketId,
  ...props
}: FlexProps & { defaultRoom: string; ticketId: number | null }) => {
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [input, setInput] = useState<string>("");
  const toast = useSimpleToast();
  const user = useConnectedUser();
  const meta: TicketMeta = useMemo(
    () => ({
      room: {
        name: defaultRoom,
      },
      author: {
        id: user ? user.id : null,
      },
      ticket: {
        id: ticketId,
      },
    }),
    [user, defaultRoom, ticketId]
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const ticket = useTicket(ticketId ?? undefined);

  useEffect(() => {
    const onConnect = () => {
      setIsConnected(true);
      socket.emit("join", meta);
    };

    const onDisconnect = () => {
      setIsConnected(false);
      toast.error("Déconnexion", "Vous venez d'être déconnecté du chat");
    };

    const onMessage = (meta: TicketMeta, message: string) => {
      setMessages([
        ...messages,
        {
          content: message,
          createdAt: new Date(),
          updatedAt: new Date(),
          authorId: meta.author.id ?? 0,
        },
      ]);
    };

    const onHistory = (_meta: TicketMeta, messages: TicketMessage[]) => {
      setMessages(messages);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("message", onMessage);
    socket.on("history", onHistory);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("message", onMessage);
      socket.off("history", onHistory);
    };
  });

  useEffect(() => {
    socket.emit("join", meta);
    socket.emit("history", meta);
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.code === "Enter" &&
      event.shiftKey === false &&
      input.trim() !== ""
    ) {
      try {
        socket.emit("message", meta, input);
        setInput("");
        event.preventDefault();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleSend = () => {
    socket.emit("message", meta, input);
    setInput("");
  };

  const queryClient = useQueryClient();
  const { mutate: mutateLinkFilesToTicket } = useMutation(linkFilesToTicket, {
    onSuccess: () => {
      queryClient.invalidateQueries("tickets");
      queryClient.invalidateQueries(["tickets", "logs"]);
      queryClient.invalidateQueries(["tickets", "documents"]);
      socket.emit("history", meta)
    },
  });
  const handleAttachFile = (files: FileList) => {
    if (ticketId) {
      mutateLinkFilesToTicket({
        id: ticketId,
        fileList: files,
      });
    }
  };
  const changeStatusModal = useDisclosure();
  const proofModal = useDisclosure();
  const changeStatusForm = useRef<TicketStatusFormHandler>(null);
  const statusRef = useRef<TicketStatus | null | undefined>(ticket?.status);
  const discountRef = useRef<number | null | undefined>(ticket?.discount);
  const { mutate: mutateUpdateTicketStatus } = useMutation(updateTicket, {
    onSuccess: () => {
      queryClient.invalidateQueries("tickets");
      queryClient.invalidateQueries(["tickets", "logs"]);
      queryClient.invalidateQueries(["tickets", "documents"]);
      changeStatusModal.onClose();
    },
  });
  return (
    <>
      <Grid
        w={props.w ?? "100%"}
        maxW={props.maxW}
        h={"100%"}
        gridTemplateRows={"100px 1fr"}
        position={props.position}
        top={props.top}
        right={props.right}
        flexDirection={"column"}
      >
        <Flex
          borderLeft="solid 1px"
          borderBottom="solid 1px"
          borderColor="gray.200"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text
            lineHeight={"6rem"}
            fontSize={"18px"}
            fontWeight={"600"}
            color={"black"}
            ml={8}
          >
            Objet du ticket
          </Text>
          { userIsAdmin(user) && <Button
            bgColor="brand.600"
            color="white"
            mr={8}
            onClick={changeStatusModal.onOpen}
          >
            Changer le statut
          </Button> }
        </Flex>

        <Box h={"100%"} w={props.w ?? "100%"} maxW={props.maxW}>
          {!isConnected && (
            <Flex
              w="100%"
              h="100%"
              justifyContent="center"
              alignItems="center"
              direction={"column"}
            >
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="brand.700"
                size="xl"
              ></Spinner>
              <Text mt={4} lineHeight="21px" fontSize="18px" fontWeight="600">
                Connexion au chat
              </Text>
            </Flex>
          )}
          {isConnected && (
            <Box h={"100%"} w="100%" maxW={props.maxW}>
              <Box
                h={"100%"}
                maxH={"89vh"}
                w={"100%"}
                maxW={props.maxW}
                overflow={"auto"}
                bgColor={"gray.200"}
              >
                {messages && <ChatMessages messages={messages} />}
                <Box h="200px"></Box>
              </Box>
              <Flex
                position={"absolute"}
                bottom={10}
                right={"20px"}
                zIndex={1000}
                w="calc(100% - 40px)"
                p={1}
                alignItems={"end"}
                backgroundColor={"white"}
                borderRadius={"xl"}
                boxShadow="md"
              >
                <Textarea
                  placeholder="Ecrivez un message"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  border={"none"}
                  h="140px"
                ></Textarea>
                <Flex pb={2}>
                  <AttachedFileIcon
                    mx={4}
                    onClick={() => onOpen()}
                    color="gray.400"
                    w="20px"
                    h="20px"
                  ></AttachedFileIcon>
                  <SendIcon
                    mx={4}
                    onClick={handleSend}
                    color="gray.400"
                    w="20px"
                    h="20px"
                  ></SendIcon>
                </Flex>
              </Flex>
            </Box>
          )}
        </Box>

        <SimpleModal
          isOpen={changeStatusModal.isOpen}
          onClose={changeStatusModal.onClose}
          title="Modifier le statut"
          icon={<LabelsIcon color="brand.600" />}
          acceptText="Sauvegarder"
          onClick={async () => {
            if (changeStatusForm.current) {
              const valid = await changeStatusForm.current.valid();
              if (!valid) {
                return;
              }
              const values = changeStatusForm.current.values();
              if (
                values.status === TicketStatus.CLOSED &&
                ticket?.ticketLabel.proof
              ) {
                proofModal.onOpen();
                statusRef.current = values.status;
                discountRef.current = values.discount;
                changeStatusModal.onClose();
              } else {
                if (values && ticketId) {
                  mutateUpdateTicketStatus({
                    id: ticketId,
                    status: values.status === "" ? undefined : values.status,
                    discount: values.discount,
                  });
                }
              }
            }
          }}
        >
          <TicketStatusForm
            ref={changeStatusForm}
            value={{
              status: ticket ? ticket.status : "",
              discount: ticket?.discount ?? undefined,
            }}
          />
        </SimpleModal>

        <SimpleModal
          isOpen={proofModal.isOpen}
          onClose={proofModal.onClose}
          acceptText="Confirmer"
          title="Confirmation de preuve"
          onClick={async () => {
            proofModal.onClose()
            if (ticketId && statusRef.current != null && discountRef.current != null) {
              mutateUpdateTicketStatus({
                id: ticketId,
                status: statusRef.current,
                discount: discountRef.current,
              });
            }
          }}
        >
          <Text>Ce ticket a besoin d'une preuve pour être fermé.</Text>
          <Text>Pouvez-vous confirmer qu'une preuve a été donnée ?</Text>
        </SimpleModal>
      </Grid>

      <AttachedFileModal
        isOpen={isOpen}
        onClose={onClose}
        onClick={handleAttachFile}
      ></AttachedFileModal>
    </>
  );
};

export default Chat;
